import { useCurrentUser, useNotifications } from '@bpm-web-app/swr-hooks';
import { HistoryContext, useHubSwitch, useOnClickOutside, useViewport } from '@bpm-web-app/utils';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Notification } from '@bpm-web-app/supreme-api-sdk';

// Icons
import CloseIcon from '../../assets/icons/close.svg';
import NavIcon from '../../assets/icons/nav-icon.svg';
import BPMLogo from '../../assets/icons/bpm-logo.svg';
import { ReactComponent as PlusIcon } from '../../assets/icons/add.svg';

import { NavContext } from '../nav/nav.context';
import { useSession } from '../shared/session-provider/session-provider';
import { UserNotifications } from '../user-notifications/user-notifications';
import mainHeaderStyles from './supreme-header.module.css';
import { ProfileNavItems } from './profile-nav-items';
import { PlatformSwitch } from '../shared/platform-switch/platform-switch';
import { ArtistPortalHeaderContext } from './artist-portal-header.context';
import { SquaredButton } from '../shared/squared-button/squared-button';
import { AppLink } from '../shared';
import { ExpandNavButton } from './expand-nav-button';
import { HeaderProfileButton } from './header-profile-button/header-profile-button';
import { HeaderNotificationButton } from './header-notification-button/header-notification-button';
import { HeaderPlatformsButton } from './header-platforms-button/header-platforms-button';

export function ArtistPortalHeader() {
    const { user } = useSession();
    const { isArtistPlatform } = useHubSwitch();

    const { goBack, goForwards, canGoBack, canGoForwards } = useContext(HistoryContext);

    const router = useRouter();

    const { data: userData } = useCurrentUser();
    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
    const [showPlatformSwitch, setShowPlatformSwitch] = useState(false);
    const { isNavOpen, openNav, closeNav, isNavHidden } = useContext(NavContext);
    const { isSubmitTrackButtonHidden } = useContext(ArtistPortalHeaderContext);

    const { data: notifications } = useNotifications([Notification.PlatformEnum.Artist]);
    const [showNotifications, setShowNotifications] = useState(false);
    const { isMobile: isMobileViewPort } = useViewport();
    const { ref: notificationElRef } = useOnClickOutside<HTMLDivElement>(false, () => (isMobileViewPort ? null : setShowNotifications(false)));

    const desktopMenuOpenerRef = useRef<HTMLDivElement>(null);
    const { ref: profileMenuRef } = useOnClickOutside<HTMLDivElement>(false, (e: MouseEvent) => {
        if (!desktopMenuOpenerRef.current?.contains(e.target as Node)) {
            setIsProfileMenuOpen(false);
        }
    });

    const desktopSwitchPlatformOpenerRef = useRef<HTMLDivElement>(null);
    const { ref: switchPlatformMenuRef } = useOnClickOutside<HTMLDivElement>(false, (e: MouseEvent) => {
        if (!desktopSwitchPlatformOpenerRef.current?.contains(e.target as Node)) {
            setShowPlatformSwitch(false);
        }
    });

    const hasNotifications = useMemo(() => notifications?.data?.some((notification) => !notification.read), [notifications?.data]);

    const closeIcon = useMemo(() => <CloseIcon />, []);
    const navIcon = useMemo(() => <NavIcon />, []);

    useEffect(() => {
        setIsProfileMenuOpen(false);
    }, [router]);

    const handleMenuToggle = () => {
        if (isMobileViewPort) closeNav();
        setIsProfileMenuOpen(!isProfileMenuOpen);
    };

    return (
        <div className={mainHeaderStyles['supreme-header']}>
            <header className={classNames(mainHeaderStyles['supreme-header__container'], { [mainHeaderStyles['supreme-header__container--nav-opened']]: isNavOpen })}>
                <ExpandNavButton
                    onOpenNav={openNav}
                    isMobile={isMobileViewPort}
                    isNavHidden={isNavHidden}
                    isNavOpen={isNavOpen}
                />
                {!isSubmitTrackButtonHidden ? (
                    <button
                        type="button"
                        aria-label="Open Nav"
                        onClick={() => {
                            if (isNavOpen) {
                                setShowNotifications(false);
                                closeNav();
                            } else {
                                openNav();
                            }
                        }}
                        className={mainHeaderStyles['supreme-header__mobile-nav-btn']}
                    >
                        {isNavOpen ? closeIcon : navIcon}
                    </button>)
                    : <div />}
                {isMobileViewPort ? (
                    <div className={mainHeaderStyles['supreme-header__logo']}>
                        <AppLink href="/">
                            <a aria-label="Homepage" className={mainHeaderStyles['supreme-header__logo-img']}>
                                <BPMLogo />
                            </a>
                        </AppLink>
                    </div>
                ) : null}
                <div className={mainHeaderStyles['supreme-header__center']}>
                    <button
                        type="button"
                        disabled={!canGoBack}
                        className={classNames(mainHeaderStyles['supreme-header__history'], mainHeaderStyles['supreme-header__history--back'])}
                        aria-label="Previous Page"
                        onClick={goBack}
                    >
                        <i className="icon icon--chevron-left icon--light" />
                    </button>
                    <button
                        type="button"
                        disabled={!canGoForwards}
                        className={classNames(mainHeaderStyles['supreme-header__history'], mainHeaderStyles['supreme-header__history--forward'])}
                        aria-label="Next Page"
                        onClick={goForwards}
                    >
                        <i className="icon icon--chevron-right icon--light" />
                    </button>
                </div>
                <div className={mainHeaderStyles['supreme-header__right']}>
                    {!isSubmitTrackButtonHidden ? (
                        <div className={mainHeaderStyles['supreme-header__submit-track']}>
                            <SquaredButton type="filled" leftIcon={<PlusIcon height={26} width={26} />} label="Submit Track" onPress={() => router.push('/submit-track')} />
                        </div>
                    ) : null
                    }

                    {!isSubmitTrackButtonHidden ? (
                        <div>
                            <div ref={desktopSwitchPlatformOpenerRef}>
                                <HeaderPlatformsButton onClick={() => setShowPlatformSwitch(!showPlatformSwitch)} isOpen={showPlatformSwitch} />
                            </div>
                            <div ref={switchPlatformMenuRef}>
                                {showPlatformSwitch ? <PlatformSwitch
                                    platforms={[
                                        { name: 'BPM Create' },
                                        { name: 'BPM Supreme' },
                                    ]}
                                    onClose={() => {
                                        setShowPlatformSwitch(false);
                                    }}
                                /> : null}
                            </div>
                        </div>
                    ) : null}
                    {!isSubmitTrackButtonHidden ? (
                        <div ref={notificationElRef}>
                            <HeaderNotificationButton
                                onClick={() => setShowNotifications((prevState) => !prevState)}
                                hasNotifications={hasNotifications}
                                isShowingNotifications={showNotifications}
                            />
                            <UserNotifications
                                platform={Notification.PlatformEnum.Artist}
                                onClose={() => {
                                    setShowNotifications(false);
                                }}
                                isVisible={showNotifications && !isMobileViewPort} />
                        </div>
                    ) : null}

                    <div ref={desktopMenuOpenerRef}>
                        <HeaderProfileButton
                            isOpen={isProfileMenuOpen}
                            isHidden={isSubmitTrackButtonHidden}
                            imageAlt={userData?.data?.user?.full_name}
                            imageUrl={userData?.data?.user?.profile_image_thumbnail_url}
                            onClick={handleMenuToggle}
                        />
                    </div>
                </div>
                <HeaderProfileButton
                    imageAlt={userData?.data?.user?.full_name}
                    imageUrl={userData?.data?.user?.profile_image_thumbnail_url}
                    onClick={handleMenuToggle}
                    isMobile
                    isOpen={isProfileMenuOpen}
                    hasNotifications={hasNotifications}
                />
            </header>
            <div
                ref={profileMenuRef}
                className={classNames(mainHeaderStyles['supreme-header__user-menu'], {
                    [mainHeaderStyles['supreme-header__user-menu--active']]: isProfileMenuOpen,
                    [mainHeaderStyles['supreme-header__fullscreen-mobile']]: isArtistPlatform,
                })}
            >
                <div className={mainHeaderStyles['supreme-header__user-menu-header']}>
                    <div className={mainHeaderStyles['supreme-header__user-infos']}>
                        <div className={mainHeaderStyles['supreme-header__user-image']}>
                            <HeaderProfileButton
                                onlyDisplayImage
                                isHidden={!isMobileViewPort}
                                imageAlt={userData?.data?.user?.full_name}
                                imageUrl={userData?.data?.user?.profile_image_thumbnail_url}
                            />
                        </div>
                        <div className={mainHeaderStyles['supreme-header__user-info']}>
                            <div className={mainHeaderStyles['supreme-header__user-name']}>{`${user?.first_name} ${user?.last_name}`}</div>
                            <div className={mainHeaderStyles['supreme-header__user-id']}>{`Customer # ${user?.id}`}</div>
                        </div>
                        <div className={mainHeaderStyles['supreme-header__notification-mobile-btn']}>
                            <HeaderNotificationButton
                                isMobile
                                onClick={() => setShowNotifications((prevState) => !prevState)}
                                hasNotifications={hasNotifications}
                                isShowingNotifications={showNotifications}
                            />
                            <UserNotifications
                                platform={Notification.PlatformEnum.Artist}
                                onClose={() => {
                                    setShowNotifications(false);
                                }}
                                isVisible={showNotifications && isMobileViewPort}
                                isMobile
                            />
                        </div>
                    </div>
                </div>
                <ProfileNavItems hideAddMusic />
            </div>
        </div>
    );
}

export default ArtistPortalHeader;
